import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { HeaderTranslationList } from '@1po/1po-bff-fe-spec/generated/catalog/maintenance_plan/response/GetMaintenancePlanResponse';
import { RootState } from 'app/AppStore';
import { getLastVehicleDetail, getMaintenancePlan } from 'domains/catalog/Catalog.store';
import { MaintenancePlanLocal } from 'domains/catalog/Catalog.types';
import { convertMileageToCurrentUnits } from 'domains/maintenancePlan/MaintenancePlan.mapper';
import { getPlannedOperations } from 'domains/maintenancePlan/MaintenancePlan.store';
import { mileage_units } from 'domains/maintenancePlan/MaintenancePlan.types';
import FluidsSection from 'pages/CatalogPage/DH/Maintenance/Fluids/FluidsSection';
import MaintenanceHeader from 'pages/CatalogPage/DH/Maintenance/MaintenanceHeader';
import MileageDisplay from 'pages/CatalogPage/DH/Maintenance/Mileage/MileageDisplay';
import MileageTimeSection from 'pages/CatalogPage/DH/Maintenance/Mileage/MileageTimeSection';
import ServiceSection from 'pages/CatalogPage/DH/Maintenance/Services/ServiceSection';
import SpecialInformation from 'pages/CatalogPage/DH/Maintenance/Services/SpecialInformation';
import { Box, Container, Flex } from 'UI';
import { getData, hasData } from 'utils';

export interface MaintenanceViewProps {
  maintenancePlanData: MaintenancePlanLocal;
  getLabel: (id: number) => string;
  units?: mileage_units;
  printView?: boolean;
  val?: string;
}

export interface MaintenanceEditProps {
  maintenancePlanData: MaintenancePlanLocal;
  getLabel: (id: number) => string;
  units: mileage_units;
  mileage?: number;
  setMileage: (mileage: number | undefined) => void;
  val: string;
  setVal: (mileage: string) => void;
  setUnits: (units: mileage_units) => void;
}

const Maintenance = () => {
  const [mileage, setMileage] = useState<number | undefined>(undefined);
  const [units, setUnits] = useState<'km' | 'miles'>('km');
  const [val, setVal] = useState(mileage ? String(mileage) : '0');
  const maintenancePlanData = getData(useSelector(getMaintenancePlan));
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const operationsResponse = useSelector((state: RootState) => getPlannedOperations(state, vehicleDetail?.vin));

  useEffect(() => {
    if (
      !hasData(operationsResponse) ||
      operationsResponse.searchStatus === 'loading' ||
      (operationsResponse.data?.operations ?? []).length === 0
    ) {
      return;
    }

    const firstItem = operationsResponse.data?.operations[0];
    const apiUnits = firstItem?.mileageUnit ?? 'km';
    const apiMileage = firstItem?.usedVehicleMileage ?? 0;

    if (units === 'km' && apiUnits === 'km') {
      setMileage(apiMileage);
      setVal(apiMileage ? String(apiMileage) : '0');
    } else if (apiUnits) {
      const mileConverted = convertMileageToCurrentUnits(units, apiUnits, apiMileage);
      setMileage(mileConverted);
      setVal(mileConverted ? String(mileConverted) : '0');
    } else {
      //
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationsResponse]);

  if (!maintenancePlanData) {
    return <></>;
  }

  const getLabel = (id: number) => {
    const label = maintenancePlanData.headerTranslationsList.find((l: HeaderTranslationList) => l.id === id);
    return label?.label ?? '';
  };

  return (
    <Container>
      <MaintenanceHeader maintenancePlanData={maintenancePlanData} getLabel={getLabel} units={units} val={val} />
      <Flex direction={'column'} gap={30}>
        <MileageDisplay
          maintenancePlanData={maintenancePlanData}
          getLabel={getLabel}
          units={units}
          mileage={mileage}
          setMileage={setMileage}
          val={val}
          setVal={setVal}
          setUnits={setUnits}
        />
        <MileageTimeSection maintenancePlanData={maintenancePlanData} getLabel={getLabel} units={units} />
        <ServiceSection maintenancePlanData={maintenancePlanData} getLabel={getLabel} />
        <SpecialInformation specialInformation={maintenancePlanData.specialInformationList} />
        <FluidsSection maintenancePlanData={maintenancePlanData} getLabel={getLabel} />
      </Flex>
      <Box height={100} />
    </Container>
  );
};

export default Maintenance;
